import type { JobApplicant } from '@factoryfixinc/ats-interfaces';
import { JobApplicantStatus } from '@factoryfixinc/ats-interfaces';

/**
 * Detect recent outreach to pros by the customer for the repeat-outreach warning.
 */

export interface FlaggedCandidate {
  profileId: number;
  name: string;
  /** The single most recent qualifying record. */
  jobApplicant: JobApplicant;
  /** Whether to surface the "View conversation" deep-link — see {@link canLinkToConversation}. */
  canLinkToConversation: boolean;
}

export interface RecentProOutreachWarning {
  flagged: FlaggedCandidate[];
  totalSelected: number;
  /** Every selected candidate is flagged → warning only, no exclude checkbox. */
  allFlagged: boolean;
  /** A subset is flagged → warning + exclude checkbox. */
  someFlagged: boolean;
}

export interface RecentProOutreachPro {
  profileId: string | number;
  nameFirst?: string;
  nameLast?: string;
}

const DEFAULT_WINDOW_DAYS = 90;
const DAY_MS = 24 * 60 * 60 * 1000;

// `Notified` is a passive one-way notification, not real engagement, so it never counts.
const EXCLUDED_STATUSES = new Set<JobApplicantStatus>([JobApplicantStatus.NOTIFIED]);

/**
 * Recency is anchored on `applyTs` — the same date the candidate row already surfaces
 * (ProApplicantList) — falling back to `createTs` for outreach records that have no
 * `applyTs` until the candidate engages.
 */
function activityTimestamp(applicant: JobApplicant): number {
  const ms = Date.parse(applicant.applyTs ?? applicant.createTs);
  return Number.isNaN(ms) ? 0 : ms;
}

function canLinkToConversation(applicant: JobApplicant): boolean {
  return Boolean(applicant.applyTs);
}

export function computeRecentProOutreachWarning(
  pros: RecentProOutreachPro[],
  getApplicants: (profileId: number) => JobApplicant[] | undefined,
  options: { now: Date; windowDays?: number },
): RecentProOutreachWarning {
  const windowDays = options.windowDays ?? DEFAULT_WINDOW_DAYS;
  const cutoff = options.now.getTime() - windowDays * DAY_MS;

  const flagged: FlaggedCandidate[] = [];
  for (const pro of pros) {
    const profileId = Number(pro.profileId);
    const qualifying = (getApplicants(profileId) ?? []).filter(
      (a) =>
        !EXCLUDED_STATUSES.has(a.status as JobApplicantStatus) && activityTimestamp(a) >= cutoff,
    );
    if (qualifying.length === 0) continue;

    const mostRecent = qualifying.reduce((best, a) =>
      activityTimestamp(a) > activityTimestamp(best) ? a : best,
    );
    flagged.push({
      profileId,
      name: `${pro.nameFirst ?? ''} ${pro.nameLast ?? ''}`.trim(),
      jobApplicant: mostRecent,
      canLinkToConversation: canLinkToConversation(mostRecent),
    });
  }

  const totalSelected = pros.length;
  return {
    flagged,
    totalSelected,
    allFlagged: flagged.length > 0 && flagged.length === totalSelected,
    someFlagged: flagged.length > 0 && flagged.length < totalSelected,
  };
}

/** "Jorge, George, and Gorg" up to the cap;
 * "Jorge, George, Gorg, and N others" beyond it.
 */
export function formatWarningNames(
  flagged: Pick<FlaggedCandidate, 'name'>[],
  options: { maxNames?: number } = {},
): string {
  const maxNames = options.maxNames ?? 3;
  const names = flagged.map((f) => f.name);

  if (names.length <= maxNames) {
    if (names.length <= 1) return names[0] ?? '';
    if (names.length === 2) return `${names[0]} and ${names[1]}`;
    return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
  }

  const shown = names.slice(0, maxNames).join(', ');
  const others = names.length - maxNames;
  return `${shown}, and ${others} other${others === 1 ? '' : 's'}`;
}

/** Deep-link to the existing application/conversation*/
export function conversationLinkFor(flagged: Pick<FlaggedCandidate, 'jobApplicant'>): string {
  const { id, jobId } = flagged.jobApplicant;
  return `/jobs?jobApplicantId=${id}&jobId=${jobId}&tab=conversation`;
}
